import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { parseCssStringToObject, resolvePreviewIsMobile } from "../../../helper";
import { DEFAULT_BLOCK_CSS, POPUP_SECTIONS, PRODUCT_TO_OFFER } from "../../../constant/constant";
import { useApp } from "../../../context/AppState";
import TextBlock from "../../../components/Preview/Draggable/Block/TextBlock";
import CoverImageBlock from "../../../components/Preview/Draggable/Block/CoverImageBlock";
import QuantityPickerBlock from "../../../components/Preview/Draggable/Block/QuantityPickerBlock";
import VariantPickerBlock from "../../../components/Preview/Draggable/Block/VariantPickerBlock";
import PriceBlock from "../../../components/Preview/Draggable/Block/PriceBlock";
import {
  ackSelectBlockToParent,
  parseSelectBlockMessage,
} from "../../../utils/preview-selection-messages";
import { isSectionVisible } from "../../../../shared/section-visibility.js";
import {
  applyCompactExtensionWidgetSubsectionDefaults,
  applyExtensionSliderSubsectionDefaults,
  applyExtensionWidgetSubsectionDefaults,
} from "../../../../shared/extension-widget-display-defaults.js";
import { findProductSubsectionByType } from "../../../../shared/post-purchase-product-sections.js";
import ProductDescriptionBlock from "./ProductDescriptionBlock";
import {
  generateDummyProducts,
  getDummyProductCount,
  padProductsForPreviewSlots,
  shouldShowDummyProducts,
} from "../../../utils/dummyProductGenerator";
import {
  isExtensionListV2Shell,
  isExtensionSliderShell,
  resolveExtensionProductShell,
  resolveExtensionSliderItemsPerRow,
  resolveExtensionSliderTileLayout,
} from "../../../../shared/extension-product-layout.js";
import {
  DESCRIPTION_LAYOUT,
  getDescriptionDisplay,
  resolveProductDescriptionText,
  stripHtmlToPlainText,
} from "../../../../shared/post-purchase-product-sections.js";
import styles from "./PostPurchaseProductBlock.module.css";

/**
 * Post-purchase product layout — kept separate from ProductBlock so checkout/other
 * templates are unaffected.
 */
export default function PostPurchaseProductBlock({
  popupData,
  section,
  activeStep,
  parentIndex,
  footerContent = null,
  layout = "grid",
  extensionVariant = "default",
}) {
  const { appState, isMobile: viewportIsMobile, previewDeviceIsMobile } = useApp();
  const isMobile = resolvePreviewIsMobile(
    appState,
    viewportIsMobile,
    previewDeviceIsMobile
  );
  const [product, setProduct] = useState(section?.products || []);
  const [selectedBlock, setSelectedBlock] = useState({});

  const offerType =
    popupData?.generalSettings?.condition?.product?.products_to_offer?.type ||
    PRODUCT_TO_OFFER.SPECIFIC_PRODUCT;

  const previewUsesDummyProducts = useMemo(
    () => appState?.is_preview_mode && shouldShowDummyProducts(offerType),
    [appState?.is_preview_mode, offerType]
  );

  const extensionShell = useMemo(
    () => resolveExtensionProductShell(popupData, section),
    [popupData, section]
  );
  const isExtensionSliderLayout =
    layout === "extension" && isExtensionSliderShell(extensionShell);
  const isExtensionListV2Layout =
    layout === "extension" && isExtensionListV2Shell(extensionShell);
  const extensionSliderItemsPerRow = useMemo(
    () => resolveExtensionSliderItemsPerRow(section, popupData),
    [section?.setting?.layout?.itemsPerRow, section?.setting?.layout?.shell, popupData]
  );
  const extensionSliderScrollRef = useRef(null);
  const [extensionSliderViewportWidth, setExtensionSliderViewportWidth] = useState(0);
  const extensionSliderTileLayout = useMemo(
    () =>
      resolveExtensionSliderTileLayout({
        containerWidth:
          extensionSliderViewportWidth > 0 ? extensionSliderViewportWidth : undefined,
        itemsPerRow: extensionSliderItemsPerRow,
        gap: 12,
        scrollPadding: 0,
        popupOrGeneralSettings: popupData,
      }),
    [extensionSliderItemsPerRow, extensionSliderViewportWidth, popupData]
  );

  useEffect(() => {
    if (!isExtensionSliderLayout) return undefined;

    const node = extensionSliderScrollRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const syncWidth = () => {
      const nextWidth = Math.round(node.getBoundingClientRect().width);
      setExtensionSliderViewportWidth((previous) =>
        previous === nextWidth ? previous : nextWidth
      );
    };

    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isExtensionSliderLayout, product.length, extensionSliderItemsPerRow]);
  const [sliderExpandedDescriptions, setSliderExpandedDescriptions] = useState([]);

  useEffect(() => {
    if (previewUsesDummyProducts) {
      const count = getDummyProductCount(popupData, activeStep);
      setProduct(generateDummyProducts(count));
      return;
    }

    // Checkout / thank-you / order-status compact widgets + post-purchase grid:
    // always pad empty slots in admin preview so the widget is visible before a product is selected.
    if (layout === "extension" || appState?.is_preview_mode) {
      const count = getDummyProductCount(popupData, activeStep);
      setProduct(padProductsForPreviewSlots(section?.products || [], count));
      return;
    }

    setProduct(section?.products || []);
  }, [
    section?.products,
    previewUsesDummyProducts,
    popupData,
    activeStep,
    offerType,
    layout,
    appState?.is_preview_mode,
    popupData?.generalSettings?.condition?.product?.products_to_offer,
    popupData?.generalSettings?.condition?.product?.products_to_offer_limit,
    popupData?.generalSettings?.productShellLayout,
    section?.setting?.layout?.shell,
    section?.setting?.layout?.itemsPerRow,
  ]);

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
  };

  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const handleBlockClick = useCallback(
    (type, subSectionIndex) => {
      if (!appState?.is_preview_mode) return;
      const prev = selectedBlockRef.current;
      if (prev.type === type && prev.index === subSectionIndex) return;
      ackSelectBlockToParent({
        type,
        index: parentIndex,
        subIndex: subSectionIndex,
      });
      setSelectedBlock({ type, index: subSectionIndex });
    },
    [appState?.is_preview_mode, parentIndex]
  );

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (data.event === "sectionCreated") {
        const { type, subIndex: subSectionIndex } = data;
        if (typeof subSectionIndex === "number" && subSectionIndex > -1) {
          setSelectedBlock({ type, index: subSectionIndex });
          handleBlockClick(type, subSectionIndex);
        }
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection) return;
      if (selection.index !== parentIndex) return;
      if (typeof selection.subIndex !== "number" || selection.subIndex < 0) return;

      setSelectedBlock({
        type: selection.type,
        index: selection.subIndex,
      });
      ackSelectBlockToParent({
        type: selection.type,
        index: parentIndex,
        subIndex: selection.subIndex,
      });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [parentIndex, handleBlockClick]);

  const getSubBlockCss = useCallback(
    (productSection) => ({
      ...DEFAULT_BLOCK_CSS,
      ...productSection?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
      ...parseCssStringToObject(
        productSection?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
      ),
    }),
    [isMobile]
  );

  const getSectionByType = useCallback(
    (type, { visibleOnly = false } = {}) =>
      findProductSubsectionByType(section?.sections, type, { visibleOnly })?.section ??
      null,
    [section?.sections]
  );

  const findSubsectionIndex = useCallback(
    (type, { visibleOnly = false } = {}) =>
      findProductSubsectionByType(section?.sections, type, { visibleOnly })?.index ?? -1,
    [section?.sections]
  );

  const sliderDescriptionSection = useMemo(
    () =>
      findProductSubsectionByType(section?.sections, POPUP_SECTIONS.DESCRIPTION, {
        visibleOnly: true,
      })?.section ?? null,
    [section?.sections]
  );

  const sliderProductDescriptions = useMemo(() => {
    if (!isExtensionSliderLayout || !sliderDescriptionSection) {
      return [];
    }
    return product.map((productData) =>
      stripHtmlToPlainText(
        resolveProductDescriptionText(productData, sliderDescriptionSection)
      )
    );
  }, [isExtensionSliderLayout, product, sliderDescriptionSection]);

  const sliderDescriptionDisplay = useMemo(
    () =>
      sliderDescriptionSection
        ? getDescriptionDisplay(sliderDescriptionSection)
        : null,
    [sliderDescriptionSection]
  );

  useEffect(() => {
    if (!isExtensionSliderLayout) return;

    const useDefaultExpanded =
      sliderDescriptionDisplay?.layout === DESCRIPTION_LAYOUT.COLLAPSIBLE &&
      sliderDescriptionDisplay.defaultExpanded;

    setSliderExpandedDescriptions(
      product.map((_productData, index) => {
        if (!useDefaultExpanded) return false;
        return Boolean(sliderProductDescriptions[index]);
      })
    );
  }, [
    isExtensionSliderLayout,
    product,
    sliderDescriptionDisplay,
    sliderProductDescriptions,
  ]);

  const renderSubSection = useCallback(
    (productData, productSection, productSectionIndex, productIndex = 0) => {
      if (!productSection) return null;
      if (!isSectionVisible(productSection)) return null;

      const resolvedSection =
        layout === "extension"
          ? isExtensionSliderLayout
            ? applyExtensionSliderSubsectionDefaults(productSection)
            : applyCompactExtensionWidgetSubsectionDefaults(productSection)
          : applyExtensionWidgetSubsectionDefaults(productSection, popupData);

      const css = getSubBlockCss(resolvedSection);
      if (css?.display === "none") return null;

      const isFlowControl =
        resolvedSection?.type === POPUP_SECTIONS.QUANTITY_PICKER ||
        resolvedSection?.type === POPUP_SECTIONS.VARIANT_PICKER;

      const isSubSelected =
        appState?.is_preview_mode &&
        selectedBlock?.type === resolvedSection?.type &&
        selectedBlock?.index === productSectionIndex;

      let content = null;
      switch (resolvedSection?.type) {
        case POPUP_SECTIONS.TITLE:
        case POPUP_SECTIONS.TEXT:
          content = (
            <TextBlock
              section={resolvedSection}
              parentData={productData}
              popupData={popupData}
            />
          );
          break;
        case POPUP_SECTIONS.DESCRIPTION:
          content = (
            <ProductDescriptionBlock
              section={resolvedSection}
              productData={productData}
              variant={layout === "extension" ? "extension" : "default"}
            />
          );
          break;
        case POPUP_SECTIONS.COVER_IMAGE:
          content = (
            <CoverImageBlock
              section={resolvedSection}
              productIndex={productIndex}
              productData={productData}
              popupData={popupData}
              activeStep={activeStep}
              forceCompactLayout={
                layout === "extension" && !isExtensionSliderLayout
              }
            />
          );
          break;
        case POPUP_SECTIONS.PRICE:
          content = (
            <PriceBlock
              productData={productData}
              section={resolvedSection}
              index={productSectionIndex}
              productIndex={productIndex}
              popupData={popupData}
            />
          );
          break;
        case POPUP_SECTIONS.QUANTITY_PICKER:
          content = (
            <QuantityPickerBlock
              section={resolvedSection}
              index={productSectionIndex}
              productIndex={productIndex}
              popupData={popupData}
              activeStep={activeStep}
              stretchFullWidth
            />
          );
          break;
        case POPUP_SECTIONS.VARIANT_PICKER:
          content = (
            <VariantPickerBlock
              popupData={popupData}
              section={resolvedSection}
              index={productSectionIndex}
              productIndex={productIndex}
              productData={productData}
            />
          );
          break;
        default:
          return null;
      }

      const isExtensionCover =
        layout === "extension" &&
        resolvedSection?.type === POPUP_SECTIONS.COVER_IMAGE;

      return (
        <div
          key={`${parentIndex}-0-${productSectionIndex}`}
          className={
            isExtensionCover
              ? `${styles.extensionCoverWrap}${
                  isSubSelected ? ` ${styles.extensionCoverWrapSelected}` : ""
                }`
              : undefined
          }
          style={{
            width: isExtensionCover ? undefined : "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            ...(isFlowControl
              ? { height: "auto", minHeight: 0, alignSelf: "stretch" }
              : {}),
            ...(isSubSelected && !isExtensionCover
              ? { outline: "2px solid #ea580c", outlineOffset: 2 }
              : {}),
          }}
          onClick={() =>
            handleBlockClick(resolvedSection?.type, productSectionIndex)
          }
        >
          {content}
        </div>
      );
    },
    [
      activeStep,
      appState?.is_preview_mode,
      getSubBlockCss,
      handleBlockClick,
      parentIndex,
      layout,
      popupData,
      selectedBlock?.index,
      selectedBlock?.type,
      isExtensionSliderLayout,
    ]
  );

  if (!product?.length) return null;

  if (layout === "extension") {
    const renderExtensionProduct = (productData, productIndex) => {
      const renderProductSubSection = (subsection, subsectionIndex) =>
        renderSubSection(productData, subsection, subsectionIndex, productIndex);

      const imageSection = getSectionByType(POPUP_SECTIONS.COVER_IMAGE);
      const titleSection = getSectionByType(POPUP_SECTIONS.TITLE);
      const descSection = getSectionByType(POPUP_SECTIONS.DESCRIPTION, {
        visibleOnly: true,
      });
      const priceSection = getSectionByType(POPUP_SECTIONS.PRICE, {
        visibleOnly: true,
      });
      const variantSection = getSectionByType(POPUP_SECTIONS.VARIANT_PICKER);
      const quantitySection = getSectionByType(POPUP_SECTIONS.QUANTITY_PICKER);

      const variantCount = Array.isArray(productData?.variants)
        ? productData.variants.length
        : 0;
      const showVariantPicker =
        variantCount > 1 && variantSection && isSectionVisible(variantSection);
      const showQuantity =
        quantitySection && isSectionVisible(quantitySection);
      const showPrice = !priceSection || isSectionVisible(priceSection);
      const showButton = Boolean(footerContent);

      if (isExtensionListV2Layout) {
        const buttonInTopRow = showButton && (showVariantPicker || showQuantity);
        const buttonFullWidth = showButton && !showVariantPicker && !showQuantity;
        const controlsClassName = [
          styles.extensionListV2Controls,
          showVariantPicker && showQuantity
            ? styles.extensionListV2ControlsBoth
            : styles.extensionListV2ControlsFull,
        ]
          .filter(Boolean)
          .join(" ");

        const renderListV2Cta = (fullWidth) => (
          <div
            className={
              fullWidth
                ? styles.extensionListV2CtaFull
                : styles.extensionListV2Cta
            }
          >
            {typeof footerContent === "function"
              ? footerContent(productData)
              : footerContent}
          </div>
        );

        return (
          <div
            key={`${parentIndex}-${productIndex}-extension-widget-v2`}
            className={`${styles.extensionWidget} ${styles.extensionWidgetListV2} ${
              product.length > 1 ? styles.extensionWidgetListItem : ""
            } ${
              extensionVariant === "cart-drawer"
                ? styles.extensionWidgetCartDrawer
                : isMobile
                  ? styles.extensionWidgetMobile
                  : ""
            }`.trim()}
          >
            <div
              className={`${styles.extensionListV2TopRow} ${
                buttonInTopRow ? "" : styles.extensionListV2TopRowNoCta
              }`.trim()}
            >
              <div className={styles.extensionImageCol}>
                {renderProductSubSection(
                  imageSection,
                  findSubsectionIndex(POPUP_SECTIONS.COVER_IMAGE)
                )}
              </div>
              <div className={styles.extensionListV2Middle}>
                {renderProductSubSection(
                  titleSection,
                  findSubsectionIndex(POPUP_SECTIONS.TITLE)
                )}
                {descSection ? (
                  <div className={styles.extensionDescriptionWrap}>
                    {renderProductSubSection(
                      descSection,
                      findSubsectionIndex(POPUP_SECTIONS.DESCRIPTION, {
                        visibleOnly: true,
                      })
                    )}
                  </div>
                ) : null}
              </div>
              {showPrice ? (
                <div className={styles.extensionPriceCol}>
                  {renderProductSubSection(
                    priceSection,
                    findSubsectionIndex(POPUP_SECTIONS.PRICE, {
                      visibleOnly: true,
                    })
                  )}
                </div>
              ) : null}
              {buttonInTopRow ? renderListV2Cta(false) : null}
            </div>

            {showVariantPicker || showQuantity ? (
              <div className={controlsClassName}>
                {showVariantPicker ? (
                  <div className={styles.extensionListV2Variant}>
                    {renderProductSubSection(
                      variantSection,
                      findSubsectionIndex(POPUP_SECTIONS.VARIANT_PICKER)
                    )}
                  </div>
                ) : null}
                {showQuantity ? (
                  <div className={styles.extensionListV2Quantity}>
                    {renderProductSubSection(
                      quantitySection,
                      findSubsectionIndex(POPUP_SECTIONS.QUANTITY_PICKER)
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {buttonFullWidth ? renderListV2Cta(true) : null}
          </div>
        );
      }

      return (
        <div
          key={`${parentIndex}-${productIndex}-extension-widget`}
          className={`${styles.extensionWidget} ${
            product.length > 1 ? styles.extensionWidgetListItem : ""
          } ${
            extensionVariant === "cart-drawer"
              ? styles.extensionWidgetCartDrawer
              : isMobile
                ? styles.extensionWidgetMobile
                : ""
          } ${!showQuantity && showButton ? styles.extensionActionsButtonOnly : ""}`.trim()}
        >
          <div className={styles.extensionTopRow}>
            <div className={styles.extensionImageCol}>
              {renderProductSubSection(
                imageSection,
                findSubsectionIndex(POPUP_SECTIONS.COVER_IMAGE)
              )}
            </div>
            <div className={styles.extensionContentCol}>
              <div className={styles.extensionTitlePriceRow}>
                <div className={styles.extensionTitleCol}>
                  {renderProductSubSection(
                    titleSection,
                    findSubsectionIndex(POPUP_SECTIONS.TITLE)
                  )}
                </div>
                <div className={styles.extensionPriceCol}>
                  {renderProductSubSection(
                    priceSection,
                    findSubsectionIndex(POPUP_SECTIONS.PRICE, { visibleOnly: true })
                  )}
                </div>
              </div>
              {descSection ? (
                <div className={styles.extensionDescriptionWrap}>
                  {renderProductSubSection(
                    descSection,
                    findSubsectionIndex(POPUP_SECTIONS.DESCRIPTION, {
                      visibleOnly: true,
                    })
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {showVariantPicker ? (
            <div className={styles.extensionVariantRow}>
              {renderProductSubSection(
                variantSection,
                findSubsectionIndex(POPUP_SECTIONS.VARIANT_PICKER)
              )}
            </div>
          ) : null}

          {showQuantity || showButton ? (
            <div
              className={`${styles.extensionActionsRow} ${
                !showQuantity && showButton
                  ? styles.extensionActionsRowButtonOnly
                  : ""
              }`}
            >
              {showQuantity ? (
                <div className={styles.extensionQuantityCol}>
                  {renderProductSubSection(
                    quantitySection,
                    findSubsectionIndex(POPUP_SECTIONS.QUANTITY_PICKER)
                  )}
                </div>
              ) : null}
              {showButton ? (
                <div className={styles.extensionCtaCol}>
                  {typeof footerContent === "function"
                    ? footerContent(productData)
                    : footerContent}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    };

    if (isExtensionSliderLayout) {
      const imageSection = getSectionByType(POPUP_SECTIONS.COVER_IMAGE);
      const titleSection = getSectionByType(POPUP_SECTIONS.TITLE);
      const priceSection = getSectionByType(POPUP_SECTIONS.PRICE, {
        visibleOnly: true,
      });
      const variantSection = getSectionByType(POPUP_SECTIONS.VARIANT_PICKER);
      const quantitySection = getSectionByType(POPUP_SECTIONS.QUANTITY_PICKER);

      const showImage = !imageSection || isSectionVisible(imageSection);
      const showTitle = !titleSection || isSectionVisible(titleSection);
      const showDescriptionSection =
        sliderDescriptionSection && isSectionVisible(sliderDescriptionSection);
      const showPrice = !priceSection || isSectionVisible(priceSection);
      const showQuantity =
        quantitySection && isSectionVisible(quantitySection);
      const showVariantSection =
        !variantSection || isSectionVisible(variantSection);

      const isCollapsibleDescription =
        sliderDescriptionDisplay?.layout === DESCRIPTION_LAYOUT.COLLAPSIBLE;
      const showDescriptionToggleRow =
        showDescriptionSection &&
        isCollapsibleDescription &&
        sliderProductDescriptions.some(Boolean);
      const showDescriptionTextRow =
        showDescriptionSection &&
        sliderProductDescriptions.some(Boolean) &&
        (isCollapsibleDescription
          ? sliderExpandedDescriptions.some(Boolean)
          : sliderDescriptionDisplay?.layout === DESCRIPTION_LAYOUT.EXPANDED);
      const hasMultipleVariants = product.some(
        (productData) => (productData?.variants?.length ?? 0) > 1
      );
      const showVariantRow = showVariantSection && hasMultipleVariants;

      const resolvedDescSection = sliderDescriptionSection
        ? applyExtensionSliderSubsectionDefaults(sliderDescriptionSection)
        : null;

      const renderSliderCell = (content, productIndex) => (
        <div
          key={`${parentIndex}-slider-cell-${productIndex}`}
          className={styles.extensionSliderCell}
        >
          {content}
        </div>
      );

      const renderSliderRow = (rowKey, renderCell) => (
        <div key={rowKey} className={styles.extensionSliderRow}>
          {product.map((productData, productIndex) =>
            renderSliderCell(renderCell(productData, productIndex), productIndex)
          )}
        </div>
      );

      const toggleSliderDescription = (productIndex) => {
        setSliderExpandedDescriptions((previous) => {
          const next = [...previous];
          next[productIndex] = !next[productIndex];
          return next;
        });
      };

      return (
        <div className={styles.extensionSliderWrap}>
          <div
            ref={extensionSliderScrollRef}
            className={styles.extensionSliderScroll}
            style={{
              "--ext-items-per-row": extensionSliderItemsPerRow,
              "--ext-slider-cell-width": `${extensionSliderTileLayout.tileWidth}px`,
            }}
          >
            <div className={styles.extensionSliderGrid}>
              {showImage
                ? renderSliderRow("images", (productData, productIndex) => (
                    <div className={styles.extensionSliderImageCol}>
                      {renderSubSection(
                        productData,
                        applyExtensionSliderSubsectionDefaults(imageSection),
                        findSubsectionIndex(POPUP_SECTIONS.COVER_IMAGE),
                        productIndex
                      )}
                    </div>
                  ))
                : null}

              {showTitle
                ? renderSliderRow("titles", (productData, productIndex) =>
                    renderSubSection(
                      productData,
                      applyExtensionSliderSubsectionDefaults(titleSection),
                      findSubsectionIndex(POPUP_SECTIONS.TITLE),
                      productIndex
                    )
                  )
                : null}

              {showDescriptionToggleRow
                ? renderSliderRow("description-toggle", (productData, productIndex) => (
                    <div className={styles.extensionSliderDescription}>
                      {sliderProductDescriptions[productIndex] ? (
                        <ProductDescriptionBlock
                          section={resolvedDescSection}
                          productData={productData}
                          variant="extension"
                          mode="toggle"
                          isExpanded={Boolean(sliderExpandedDescriptions[productIndex])}
                          onToggle={() => toggleSliderDescription(productIndex)}
                        />
                      ) : (
                        <div className={styles.extensionSliderDescriptionSpacer} />
                      )}
                    </div>
                  ))
                : null}

              {showDescriptionTextRow
                ? renderSliderRow("description-text", (productData, productIndex) => (
                    <div className={styles.extensionSliderDescription}>
                      {sliderProductDescriptions[productIndex] ? (
                        <ProductDescriptionBlock
                          section={resolvedDescSection}
                          productData={productData}
                          variant="extension"
                          mode="text"
                          isExpanded={
                            isCollapsibleDescription
                              ? Boolean(sliderExpandedDescriptions[productIndex])
                              : true
                          }
                        />
                      ) : (
                        <div className={styles.extensionSliderDescriptionSpacer} />
                      )}
                    </div>
                  ))
                : null}

              {showPrice
                ? renderSliderRow("prices", (productData, productIndex) => (
                    <div className={styles.extensionSliderPrice}>
                      {renderSubSection(
                        productData,
                        applyExtensionSliderSubsectionDefaults(priceSection),
                        findSubsectionIndex(POPUP_SECTIONS.PRICE, {
                          visibleOnly: true,
                        }),
                        productIndex
                      )}
                    </div>
                  ))
                : null}

              {showVariantRow
                ? renderSliderRow("variants", (productData, productIndex) => (
                    <div className={styles.extensionSliderVariant}>
                      {renderSubSection(
                        productData,
                        applyExtensionSliderSubsectionDefaults(variantSection),
                        findSubsectionIndex(POPUP_SECTIONS.VARIANT_PICKER),
                        productIndex
                      )}
                    </div>
                  ))
                : null}

              {showQuantity
                ? renderSliderRow("quantities", (productData, productIndex) => (
                    <div className={styles.extensionSliderQuantity}>
                      {renderSubSection(
                        productData,
                        applyExtensionSliderSubsectionDefaults(quantitySection),
                        findSubsectionIndex(POPUP_SECTIONS.QUANTITY_PICKER),
                        productIndex
                      )}
                    </div>
                  ))
                : null}

              {footerContent
                ? renderSliderRow("buttons", () => (
                    <div className={styles.extensionSliderCta}>{footerContent}</div>
                  ))
                : null}
            </div>
          </div>
        </div>
      );
    }

    return <>{product.map((productData, productIndex) => renderExtensionProduct(productData, productIndex))}</>;
  }

  return (
    <div style={blockCSS}>
      {product.map((productData, productIndex) => {
        const imageSection = getSectionByType(POPUP_SECTIONS.COVER_IMAGE);
        const titleSection = getSectionByType(POPUP_SECTIONS.TITLE);
        const descSection = getSectionByType(POPUP_SECTIONS.DESCRIPTION, {
          visibleOnly: true,
        });
        const priceSection = getSectionByType(POPUP_SECTIONS.PRICE, {
          visibleOnly: true,
        });
        const descIndex = findSubsectionIndex(POPUP_SECTIONS.DESCRIPTION, {
          visibleOnly: true,
        });
        const variantSection = getSectionByType(POPUP_SECTIONS.VARIANT_PICKER);
        const quantitySection = getSectionByType(POPUP_SECTIONS.QUANTITY_PICKER);

        const variantCount = Array.isArray(productData?.variants)
          ? productData.variants.length
          : 0;
        const showVariantPicker =
          variantCount > 1 &&
          variantSection &&
          isSectionVisible(variantSection);

        if (layout === "inline") {
          return (
            <div
              key={`${parentIndex}-${productIndex}-inline-extension`}
              className={`${styles.inline} ${
                isMobile ? styles.inlineMobile : ""
              }`.trim()}
            >
              <div className={styles.inlineImageCol}>
                {renderSubSection(
                  productData,
                  imageSection,
                  findSubsectionIndex(POPUP_SECTIONS.COVER_IMAGE)
                )}
              </div>
              <div className={styles.inlineBody}>
                {renderSubSection(productData, titleSection, findSubsectionIndex(POPUP_SECTIONS.TITLE))}
                {renderSubSection(
                  productData,
                  priceSection,
                  findSubsectionIndex(POPUP_SECTIONS.PRICE, { visibleOnly: true })
                )}
                {descSection
                  ? renderSubSection(productData, descSection, descIndex)
                  : null}
                {showVariantPicker
                  ? renderSubSection(
                      productData,
                      variantSection,
                      findSubsectionIndex(POPUP_SECTIONS.VARIANT_PICKER)
                    )
                  : null}
                <div className={styles.inlineActions}>
                  <div
                    className={`${styles.pickersRow} ${
                      !showVariantPicker ? styles.pickersRowQuantityOnly : ""
                    }`.trim()}
                  >
                    {renderSubSection(
                      productData,
                      quantitySection,
                      findSubsectionIndex(POPUP_SECTIONS.QUANTITY_PICKER)
                    )}
                  </div>
                  {footerContent ? (
                    <div className={styles.afterPickers}>
                      {typeof footerContent === "function"
                        ? footerContent(productData)
                        : footerContent}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            key={`${parentIndex}-${productIndex}-post-purchase`}
            className={`${styles.grid} ${
              isMobile ? styles.mobileGrid : ""
            }`.trim()}
          >
            <div className={styles.imageCol}>
              {renderSubSection(productData, imageSection, findSubsectionIndex(POPUP_SECTIONS.COVER_IMAGE))}
            </div>
            <div className={styles.detailsCol}>
              {renderSubSection(productData, titleSection, findSubsectionIndex(POPUP_SECTIONS.TITLE))}
              {renderSubSection(
                productData,
                priceSection,
                findSubsectionIndex(POPUP_SECTIONS.PRICE, { visibleOnly: true })
              )}
              {descSection
                ? renderSubSection(productData, descSection, descIndex)
                : null}
              <div
                className={`${styles.pickersRow} ${
                  isMobile ? styles.mobilePickers : ""
                } ${
                  !showVariantPicker ? styles.pickersRowQuantityOnly : ""
                }`.trim()}
              >
                {showVariantPicker
                  ? renderSubSection(
                      productData,
                      variantSection,
                      findSubsectionIndex(POPUP_SECTIONS.VARIANT_PICKER)
                    )
                  : null}
                {renderSubSection(
                  productData,
                  quantitySection,
                  findSubsectionIndex(POPUP_SECTIONS.QUANTITY_PICKER)
                )}
              </div>
              {footerContent ? (
                <div className={styles.afterPickers}>
                  {typeof footerContent === "function"
                    ? footerContent(productData)
                    : footerContent}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

PostPurchaseProductBlock.propTypes = {
  popupData: PropTypes.object,
  section: PropTypes.object,
  activeStep: PropTypes.number,
  parentIndex: PropTypes.number,
  footerContent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  layout: PropTypes.oneOf(["grid", "inline", "extension"]),
  extensionVariant: PropTypes.oneOf(["default", "cart-drawer"]),
};
